import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { CommunicationToApprove } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import { InputText, ValidatedText, InputSelect, DataSourceLookup, InputBool, DisplayBool, InputDateTime, DateComp } from '@/components';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class CommunicationToApproveGuiDef extends GuiDef<CommunicationToApprove> {
    constructor() {
        super('CommunicationToApprove');

        const Common = storeToRefs(useCommonStore());
        const CommunicationTypeSource = computed(() => {
            return Common.CommunicationTypeAsSelectLine.value;
        });

        const Name = new FieldDef<CommunicationToApprove>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'communicationtoapprove.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (communicationtoapprove) => communicationtoapprove.Name;
        Name.setValue = (communicationtoapprove, value) => (communicationtoapprove.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const CommunicationType = new FieldDef<CommunicationToApprove>();
        CommunicationType.Name = 'CommunicationType';
        CommunicationType.FieldName = 'CommunicationType';
        CommunicationType.DefinedFor = 'Both';
        CommunicationType.TranslationKey = 'communicationtoapprove.communicationtype';
        CommunicationType.Editable = true;
        CommunicationType.Filterable = true;
        CommunicationType.Sortable = true;
        CommunicationType.CssClasses = '';
        CommunicationType.value = (communicationtoapprove) => communicationtoapprove.CommunicationType;
        CommunicationType.setValue = (communicationtoapprove, value) => (communicationtoapprove.CommunicationType = value);
        CommunicationType.DetailEditor = markRaw(InputSelect);
        CommunicationType.DetailEditorProps = { datasource: { mapsource: CommunicationTypeSource } };
        CommunicationType.CellEditor = markRaw(InputSelect);
        CommunicationType.CellEditorProps = { datasource: { mapsource: CommunicationTypeSource } };
        // @ts-expect-error @ts-ignore
        CommunicationType.CellRenderer = markRaw(DataSourceLookup);
        CommunicationType.CellRendererProps = { datasource: { mapsource: CommunicationTypeSource } };
        CommunicationType.ExportValueFormatter = (obj: CommunicationToApprove) => {
            return CommunicationTypeSource.value.get(obj.CommunicationType)?.text;
        };
        CommunicationType.Validator = null;
        CommunicationType.SortDirection = undefined;
        CommunicationType.cellClassRules = undefined;
        CommunicationType.MinWidth = undefined;
        CommunicationType.MaxWidth = undefined;
        CommunicationType.Tooltip = null;
        this.Fields.push(CommunicationType);

        const Sender = new FieldDef<CommunicationToApprove>();
        Sender.Name = 'Sender';
        Sender.FieldName = 'Sender';
        Sender.DefinedFor = 'Both';
        Sender.TranslationKey = 'communicationtoapprove.sender';
        Sender.Editable = true;
        Sender.Filterable = true;
        Sender.Sortable = true;
        Sender.CssClasses = '';
        Sender.value = (communicationtoapprove) => communicationtoapprove.Sender;
        Sender.setValue = (communicationtoapprove, value) => (communicationtoapprove.Sender = value);
        Sender.DetailEditor = markRaw(InputText);
        Sender.DetailEditorProps = {};
        Sender.CellEditor = markRaw(InputText);
        Sender.CellEditorProps = {};
        Sender.CellRenderer = markRaw(ValidatedText);
        Sender.CellRendererProps = {};
        Sender.Validator = null;
        Sender.SortDirection = undefined;
        Sender.cellClassRules = undefined;
        Sender.MinWidth = undefined;
        Sender.MaxWidth = undefined;
        Sender.Tooltip = null;
        this.Fields.push(Sender);

        const Content = new FieldDef<CommunicationToApprove>();
        Content.Name = 'Content';
        Content.FieldName = 'Content';
        Content.DefinedFor = 'Both';
        Content.TranslationKey = 'communicationtoapprove.content';
        Content.Editable = true;
        Content.Filterable = true;
        Content.Sortable = true;
        Content.CssClasses = '';
        Content.value = (communicationtoapprove) => communicationtoapprove.Content;
        Content.setValue = (communicationtoapprove, value) => (communicationtoapprove.Content = value);
        Content.DetailEditor = markRaw(InputText);
        Content.DetailEditorProps = {};
        Content.CellEditor = markRaw(InputText);
        Content.CellEditorProps = {};
        Content.CellRenderer = markRaw(ValidatedText);
        Content.CellRendererProps = {};
        Content.Validator = null;
        Content.SortDirection = undefined;
        Content.cellClassRules = undefined;
        Content.MinWidth = undefined;
        Content.MaxWidth = undefined;
        Content.Tooltip = null;
        this.Fields.push(Content);

        const EventName = new FieldDef<CommunicationToApprove>();
        EventName.Name = 'EventName';
        EventName.FieldName = 'EventName';
        EventName.DefinedFor = 'Both';
        EventName.TranslationKey = 'communicationtoapprove.eventname';
        EventName.Editable = false;
        EventName.Filterable = true;
        EventName.Sortable = true;
        EventName.CssClasses = '';
        EventName.value = (communicationtoapprove) => communicationtoapprove.EventName;
        EventName.setValue = () => {};
        EventName.DetailEditor = markRaw(InputText);
        EventName.DetailEditorProps = {};
        EventName.CellEditor = markRaw(InputText);
        EventName.CellEditorProps = {};
        EventName.CellRenderer = markRaw(ValidatedText);
        EventName.CellRendererProps = {};
        EventName.Validator = null;
        EventName.SortDirection = undefined;
        EventName.cellClassRules = undefined;
        EventName.MinWidth = undefined;
        EventName.MaxWidth = undefined;
        EventName.Tooltip = null;
        this.Fields.push(EventName);

        const IsNewsletter = new FieldDef<CommunicationToApprove>();
        IsNewsletter.Name = 'IsNewsletter';
        IsNewsletter.FieldName = 'IsNewsletter';
        IsNewsletter.DefinedFor = 'Both';
        IsNewsletter.TranslationKey = 'communicationtoapprove.isnewsletter';
        IsNewsletter.Editable = false;
        IsNewsletter.Filterable = false;
        IsNewsletter.Sortable = true;
        IsNewsletter.CssClasses = '';
        IsNewsletter.value = (communicationtoapprove) => communicationtoapprove.IsNewsletter;
        IsNewsletter.setValue = () => {};
        IsNewsletter.DetailEditor = markRaw(InputBool);
        IsNewsletter.DetailEditorProps = { label: 'communicationtoapprove.isnewsletter', readonly: true };
        IsNewsletter.CellEditor = markRaw(InputBool);
        IsNewsletter.CellEditorProps = {};
        IsNewsletter.CellRenderer = markRaw(DisplayBool);
        IsNewsletter.CellRendererProps = {};
        IsNewsletter.Validator = null;
        IsNewsletter.SortDirection = undefined;
        IsNewsletter.cellClassRules = undefined;
        IsNewsletter.MinWidth = undefined;
        IsNewsletter.MaxWidth = undefined;
        IsNewsletter.Tooltip = null;
        this.Fields.push(IsNewsletter);

        const Created = new FieldDef<CommunicationToApprove>();
        Created.Name = 'Created';
        Created.FieldName = 'Created';
        Created.DefinedFor = 'Both';
        Created.TranslationKey = 'communicationtoapprove.created';
        Created.Editable = false;
        Created.Filterable = true;
        Created.Sortable = true;
        Created.CssClasses = '';
        Created.value = (communicationtoapprove) => communicationtoapprove.Created;
        Created.setValue = () => {};
        Created.DetailEditor = markRaw(InputDateTime);
        Created.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.CellEditor = markRaw(InputDateTime);
        Created.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.CellRenderer = markRaw(DateComp);
        Created.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.Validator = null;
        Created.SortDirection = undefined;
        Created.cellClassRules = undefined;
        Created.MinWidth = 180;
        Created.MaxWidth = undefined;
        Created.Tooltip = null;
        this.Fields.push(Created);

        const Changed = new FieldDef<CommunicationToApprove>();
        Changed.Name = 'Changed';
        Changed.FieldName = 'Changed';
        Changed.DefinedFor = 'Both';
        Changed.TranslationKey = 'communicationtoapprove.changed';
        Changed.Editable = false;
        Changed.Filterable = true;
        Changed.Sortable = true;
        Changed.CssClasses = '';
        Changed.value = (communicationtoapprove) => communicationtoapprove.Changed;
        Changed.setValue = () => {};
        Changed.DetailEditor = markRaw(InputDateTime);
        Changed.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Changed.CellEditor = markRaw(InputDateTime);
        Changed.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Changed.CellRenderer = markRaw(DateComp);
        Changed.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        Changed.Validator = null;
        Changed.SortDirection = undefined;
        Changed.cellClassRules = undefined;
        Changed.MinWidth = 180;
        Changed.MaxWidth = undefined;
        Changed.Tooltip = null;
        this.Fields.push(Changed);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['CommunicationType']);
    }
}
